import React, { useCallback, useState } from 'react';

import DropdownWithUpsell from '../../../../components/DropdownWithUpsell';
import useCacheExpirationOptions from './useCacheExpirationOptions';
import { useUpgradePlan } from '../../../UpgradePlan/UpgradePlanProvider';
import { usePrerenderUser } from '../../../../hooks/usePrerenderUser';
import { theme, Typography } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import ButtonWithRole from '../../../../components/ButtonWithRole';
import { CONTENT_WRITE_REFUSED_ROLES } from '../../../auth/roleCapabilities';
import { Walkthrough } from '~features/FreeTrial';
import CacheFreshnessModal from './CacheFreshnessModal';

function CacheExpirationDropdown({ onUpdateCacheFreshness }: { onUpdateCacheFreshness: (days: number) => void }) {
  const prerenderUser = usePrerenderUser();
  const cacheExpirationOptions = useCacheExpirationOptions();
  const { openUpgradePlan } = useUpgradePlan();
  const { token } = theme.useToken();

  const { updateAvailableTargets } = Walkthrough.useWalkthroughTargets();
  const [confirmCacheFreshnessModal, setConfirmCacheFreshnessModal] = useState({
    visible: false,
    value: 0,
  });

  const handleRef = useCallback((node: HTMLDivElement | null) => updateAvailableTargets('cache-settings', !!node), []);

  const onConfirmCacheFreshness = () => {
    onUpdateCacheFreshness(confirmCacheFreshnessModal.value);
    setConfirmCacheFreshnessModal({ visible: false, value: 0 });
  };

  // for value < 1, we display a confirmation modal because such intervals can incur more costs to the user
  const onChangeCacheExpiration = (value: number) => {
    if (value < 1) {
      setConfirmCacheFreshnessModal({ visible: true, value });
    } else {
      onUpdateCacheFreshness(value);
    }
  };

  return (
    <>
      <CacheFreshnessModal
        open={confirmCacheFreshnessModal.visible}
        onOk={onConfirmCacheFreshness}
        onCancel={() => setConfirmCacheFreshnessModal({ visible: false, value: 0 })}
      />
      <DropdownWithUpsell
        selectedKey={String(prerenderUser.cacheFreshness * 24)}
        onOptionSelected={(option) => {
          onChangeCacheExpiration(option.value);
        }}
        onUpsellClicked={(option) =>
          openUpgradePlan('cache-manager', option.upsellPlanId, {
            ctaLocation: 'Cache Manager - Cache Expiration Dropdown',
            ctaName: 'Upgrade',
          })
        }
        options={cacheExpirationOptions}
        styles={{ menu: { maxHeight: '13.25em', maxWidth: 385 } }}
        footerElement={
          <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: token.fontSizeSM }}>
            Shorter intervals keep fast-changing content fresh in search. <br /> Longer intervals are better for static
            pages that rarely change.
          </Typography.Paragraph>
        }
      >
        <div ref={handleRef} id="cache-settings">
          <ButtonWithRole
            disabledFor={CONTENT_WRITE_REFUSED_ROLES}
            icon={<SettingOutlined />}
            type="primary"
          >
            Cache expiration: {formatAbbreviatedInterval(prerenderUser.cacheFreshness)}
          </ButtonWithRole>
        </div>
      </DropdownWithUpsell>
    </>
  );
}

function formatAbbreviatedInterval(interval: number) {
  if (interval <= 1) {
    return `${interval * 24}h`;
  }
  return `${interval}d`;
}

export default CacheExpirationDropdown;
